import React, { useState } from "react";
import Markdown from "react-markdown";
import moment from "moment";
import Styled, { css } from "styled-components";

import LocationOnIcon from "@mui/icons-material/LocationOn";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { OverridableComponent } from "@mui/material/OverridableComponent";
import { SvgIconTypeMap } from "@mui/material/SvgIcon";
import IconDetail from "../../assets/images/icon-detail-x100.png";
import IconCancel from "../../assets/images/icon-cancel-x100.svg";

import { ITheme } from "../../interface";

import { Highlight, Title } from "../../styles/atoms/Texts";

import { useTheme } from "../../context/ThemeProvider";
import { screenSize, workExperience } from "../../constant";
const { maxWidth } = screenSize;

const ExperienceContainer = Styled.div`
  height: max-content;
  margin: 0 -1.5vw;

  @media screen and (max-width: ${maxWidth.mediumPhoneV1}){
    width: 95vw;
    margin: 0;
    position: relative;
    transform: translateX(-50%);
    left: 50%;
  }
`;

const WorkWrapper = Styled.div<{
  $props: { theme: ITheme; showMyExperience: boolean; index: number };
}>`
  ${({ $props }) => {
    const { theme, showMyExperience, index } = $props;
    const transitionDelay = (index + 1) / 10;
    return css`
      background: ${theme.colors.primary};
      color: ${theme.colors.font};
      box-shadow: ${theme.colors.tertiary} 0px 1px 0px,
        ${theme.colors.tertiary} 0px 0px 8px;
      transform: translateX(${showMyExperience ? 0 : -50}%);
      opacity: ${showMyExperience ? 1 : 0};
      transition: all 0.2s ease-in-out ${transitionDelay}s;
    `;
  }}
  margin: 17.5px 0px;
  padding: 15px 10px;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  border-radius: 4px;
  max-width: 20vw;
  max-height: 9vh;
  @media screen and (max-width: ${maxWidth.mediumTablet}) {
    max-width: 27.5vw;
    max-height: 7vh;
  }
  @media screen and (max-width: ${maxWidth.mediumPhoneV4}) {
    max-width: 35vw;
  }
  @media screen and (max-width: ${maxWidth.smallPhoneV3}) {
    flex-wrap: wrap;
    justify-content: center;
    max-width: 100vw;
    max-height: 10vh;
  }

  &.show {
    max-width: 100vw;
    max-height: 27.5vh;
    transition: all 0.2s ease-in-out 0.2s;
    @media screen and (max-width: ${maxWidth.mediumPhoneV4}) {
      max-height: 32.5vh;
    }
    @media screen and (max-width: ${maxWidth.smallPhoneV3}) {
      max-height: 50vh;
    }
  }
`;
const StyledMarkdown = Styled(Markdown)`
  pointer-events: none;
  width: 55.3vw;
  text-align: justify;
  @media screen and (max-width: ${maxWidth.mediumTablet}) {
    width: 50vw;
  }
  @media screen and (max-width: ${maxWidth.mediumPhoneV4}) {
    width: 43vw;
  }
  @media screen and (max-width: ${maxWidth.mediumPhoneV1}) {
    width: 55vw;
  }
  @media screen and (max-width: ${maxWidth.smallPhoneV3}) {
    width: 100%;
  }
  > ul {
    margin: 0;
    padding: 0 25px;
    > li{
      > ul {
        margin: 0;
        padding: 0 25px;
      }
    }
  }
`;

const CompanyLogo = Styled.img`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  @media screen and (max-width: ${maxWidth.mediumPhoneV1}) {
    width: 48px;
    height: 48px;
  }
`;
const WorkInfoContainer = Styled.div`
  transition: all 0.2s ease-in-out 0.1s;
  margin: 0px 4px;
  height: 56px;
  position: absolute;
  width: 15vw;
  left: 5vw;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  ${WorkWrapper}.show &{
    width: 71.5vw;
    @media screen and (max-width: ${maxWidth.mediumTablet}) {
      width: 70vw;
    }
    @media screen and (max-width: ${maxWidth.mediumPhoneV4}) {
      width: 68vw;
    }
    @media screen and (max-width: ${maxWidth.mediumPhoneV1}) {
      width: 82vw;
    }
    @media screen and (max-width: ${maxWidth.smallPhoneV3}) {
      width: 50vw;
    }
  }
  @media screen and (max-width: ${maxWidth.mediumTablet}) {
    margin: 0px 5px 0px 25px;
    width: 21vw;
  }
  @media screen and (max-width: ${maxWidth.mediumPhoneV4}) {
    margin: 0px 5px 0px 32px;
    width: 27vw;
  }
  @media screen and (max-width: ${maxWidth.mediumPhoneV1}) {
    height: 48px;
    width: 28vw;
  }
  @media screen and (max-width: ${maxWidth.smallPhoneV3}) {
    margin: 0;
    position: relative;
  }
  @media screen and (max-width: ${maxWidth.smallPhoneV3}) {
    width: 50vw;
  }
`;
const WorkInfoWrapper = Styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  @media screen and (max-width: ${maxWidth.mediumPhoneV1}) {
    max-width: 24vw;
  }
  @media screen and (max-width: ${maxWidth.smallPhoneV3}) {
    max-width: none;
    width: auto;
  }
`;

const WorkCompanyName = Styled.p`
  margin: 0;
  font-weight: normal;
  font-size: 17px;
  @media screen and (max-width: ${maxWidth.mediumTablet}) {
    font-size: 15px;
  }
`;
const WorkTitle = Styled.p`
  font-weight: bold;
  margin: 0;
  font-size: 14px;
  @media screen and (max-width: ${maxWidth.mediumTablet}) {
    font-size: 12.5px;
  }
`;
const WorkDuration = Styled.p`
  font-weight: normal;
  margin: 0;
  font-size: 12px;
`;

const IconExpandWrapper = Styled.div`
  margin: 0;
  width: max-content;
  height: max-content;
  z-index: 1;

  @media screen and (max-width: ${maxWidth.smallPhoneV3}) {
    position: absolute;
    left: 0;
    right: 0;
    margin: 0 auto;
    width: 57%;
    transform: translate(0, 120%);
    top: 50%;
  }
`;

const IconExpand = Styled.img<{
  $props: { theme: ITheme };
}>`
  width: 24px;
  height: 24px;
  cursor: pointer;
  z-index: 1;
  ${({ $props }) => {
    const { theme } = $props;

    return css`
      filter: ${theme.colors.cssFilterPrimary};
      &:hover {
        filter: ${theme.colors.cssFilterHighliightFont};
      }
    `;
  }}
`;

const WorkDetailContainer = Styled.div<{
  $props: { theme: ITheme };
}>`
  width: 100%;
  height: 100%;
  padding: 0 10px;
  font-size: 12px;
  margin: 0 0 0 16vw;
  display: block;
  opacity: 0;
  transition: all 0.2s ease-in-out 0.1s;

  ${WorkWrapper}.show & {
    display: block;
    opacity: 1;
  }
  @media screen and (max-width: ${maxWidth.mediumTablet}) {
    margin: 0 0 0 20vw;
  }
  @media screen and (max-width: ${maxWidth.mediumPhoneV4}) {
    margin: 0 0 0 25.5vw;
  }
  @media screen and (max-width: ${maxWidth.smallPhoneV3}) {
    margin: 5vh 0 0 0;
    flex-basis: 100%;
  }
  ${({ $props }) => {
    const { theme } = $props;

    return css`
      border-left: ${theme.colors.primary} solid 1px;
      @media screen and (max-width: ${maxWidth.smallPhoneV3}) {
        border-top: ${theme.colors.primary} solid 1px;
        border-left: none;
      }
    `;
  }}
`;

const WorkMetadataWrapper = Styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  width: max-content;
  pointer-events: none;
  ${WorkWrapper}.show &{
    z-index: 1;
    pointer-events: all;
    position: relative;
  }
  @media screen and (max-width: ${maxWidth.smallPhoneV3}) {
    width: 100%;
    justify-content: space-around;
  }
`;
const WorkMetadata = Styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const StyledIconWorkDetail = (
  icon: OverridableComponent<SvgIconTypeMap<{}, "svg">> & {
    muiName: string;
  }
) => {
  return Styled(icon)<{
    $props: { theme: ITheme };
  }>`
  ${({ $props }) => {
    const { theme } = $props;

    return css`
      color: ${theme.colors.primary};
    `;
  }}
  transition: all 0.2s ease-in-out 0s;
`;
};
const StyledIconLocation = StyledIconWorkDetail(LocationOnIcon);
const StyledIconOpenLink = StyledIconWorkDetail(OpenInNewIcon);

const WorkLink = Styled.a<{
  $props: { theme: ITheme };
}>`
  display: flex;
  flex-direction: row;
  align-items: center;
  text-decoration: none;
  cursor: pointer;
  ${({ $props }) => {
    const { theme } = $props;

    return css`
      color: inherit;
      &:hover {
        color: ${theme.colors.hightlightFont};
        > svg {
          color: ${theme.colors.hightlightFont};
        }
      }
    `;
  }}
`;

const WorkToolsWrapper = Styled.div`
  display: flex;
`;

const WorkTool = Styled.div<{
  $props: { theme: ITheme };
}>`
  margin: 5px;
  padding: 7px;
  font-size: 12px;
  border-radius: 5px;
  
  ${({ $props }) => {
    const { theme } = $props;

    return css`
      border: ${theme.colors.font} solid 1px;
    `;
  }}
`;

function MyExperience({ showMyExperience }: { showMyExperience: boolean }) {
  const { theme, oppositeTheme } = useTheme();

  const [showWork, setShowWork] = useState(
    [...Array(workExperience.length).keys()].map(() => false)
  );

  return (
    <ExperienceContainer>
      <Title $props={{ showMyExperience }}>
        My <Highlight $props={{ theme }}>Experience</Highlight>
      </Title>
      {workExperience.map((work, index) => {
        const startWork = moment(work.startDate);
        const startWorkYear = work.startDate.split("-")[0];
        const startWorkMonth = startWork.format("MMM");

        const endWork = moment(work.endDate);
        const endWorkYear = work.endDate.split("-")[0];
        const endWorkMonth = endWork.format("MMM");

        const years = endWork.diff(startWork, "year");
        startWork.add(years, "years");
        const months = endWork.diff(startWork, "months") + 1;

        const workLength = (years > 0 ? `${years} yr ` : "") + `${months} mos`;
        const workDuration = `${startWorkMonth} ${startWorkYear} - ${endWorkMonth} ${endWorkYear} · ${workLength}`;
        return (
          <WorkWrapper
            key={`exp-${index}`}
            className={showWork[index] ? "show" : ""}
            $props={{ theme: oppositeTheme, showMyExperience, index }}
          >
            <CompanyLogo src={work.companyLogo} />
            <WorkInfoContainer>
              <WorkInfoWrapper>
                <WorkCompanyName>{work.companyName}</WorkCompanyName>
                <WorkTitle>{work.title}</WorkTitle>
                <WorkDuration>{workDuration}</WorkDuration>
              </WorkInfoWrapper>
              <IconExpandWrapper>
                <IconExpand
                  onClick={() =>
                    setShowWork((prevValues) => {
                      const newValues = [...prevValues];
                      newValues[index] = !newValues[index];
                      return newValues;
                    })
                  }
                  $props={{ theme }}
                  src={showWork[index] ? IconCancel : IconDetail}
                />
              </IconExpandWrapper>
            </WorkInfoContainer>
            <WorkDetailContainer $props={{ theme }}>
              <WorkMetadataWrapper>
                <WorkMetadata>
                  <StyledIconLocation $props={{ theme }} /> {work.location}
                </WorkMetadata>
                <WorkLink
                  $props={{ theme: oppositeTheme }}
                  target="_blank"
                  rel="noopener noreferrer"
                  href={work.links.company}
                >
                  <StyledIconOpenLink $props={{ theme }} />
                  {work.links.company.replace("https://www.", "")}
                </WorkLink>
              </WorkMetadataWrapper>
              <StyledMarkdown>{work.description}</StyledMarkdown>
              <WorkToolsWrapper>
                {work.techStack.map((value) => {
                  return (
                    <WorkTool $props={{ theme: oppositeTheme }}>
                      {value}
                    </WorkTool>
                  );
                })}
              </WorkToolsWrapper>
            </WorkDetailContainer>
          </WorkWrapper>
        );
      })}
    </ExperienceContainer>
  );
}
export default MyExperience;
